import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  FileText,
  Filter,
  Info,
  UserRound,
  Users,
} from "lucide-react";
import { useGetSegment } from "../../../hooks";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/ui/collapsible";
import { useState } from "react";
import { HoverRevealText } from "@shared/components/HoverRevealText";

const SegmentDetails = ({
  openDetailSegment,
  setOpenDetailSegment,
  selectedSegment,
}: {
  openDetailSegment: boolean;
  setOpenDetailSegment: (value: boolean) => void;
  selectedSegment: any;
}) => {
  const { data: segment } = useGetSegment(
    selectedSegment?.id,
    openDetailSegment
  );
  const [openCollapsibles, setOpenCollapsibles] = useState<{
    [key: number]: boolean;
  }>({});

  const valueFields = [
    {
      label: "Audience Type",
      value: segment?.result?.audienceTypeName,
    },
    {
      label: "Created On",
      value: segment?.result?.createdDate,
    },
    {
      label: "Created By",
      value: segment?.result?.createdBy,
    },
    {
      label: "Updated On",
      value: segment?.result?.lastModifiedDate,
    },
  ];

  const handleCancel = () => {
    setOpenDetailSegment(false);
  };

  const toggleCollapsible = (index: number) => {
    setOpenCollapsibles((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Helper function to format field names
  const formatFieldName = (key: string) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div>
      {" "}
      <Dialog open={openDetailSegment} onOpenChange={setOpenDetailSegment}>
        <DialogContent className="max-w-2xl max-h-180vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              {segment?.result?.name || "New Commercial Signups"}
            </DialogTitle>
            <DialogDescription>
              Segment Details and Configuration
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {valueFields.map((field, index) => (
                <ValueLabel
                  key={index}
                  label={field.label}
                  value={field.value}
                />
              ))}
              <div className="col-span-2">
                <ValueLabel
                  label="Description"
                  value={
                    <HoverRevealText text={segment?.result?.description} side="right" />
                  }
                />
              </div>
            </div>
            <div className="mt-4">
              <hr />
            </div>
            <div className="flex justify-between gap-2">
              <div className="flex items-center gap-2 mt-4">
                <Filter className="h-4 w-4" />
                <h6 className="text-sm font-medium text-foreground">
                  Filter Criteria
                </h6>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Badge variant="outline">
                  {segment?.result?.criteriaCount} conditions
                </Badge>
              </div>
            </div>
            {segment?.result?.criteria?.map((criteria: any, index: number) => (
              <div className="mt-4" key={index}>
                <Collapsible
                  open={openCollapsibles[index] || false}
                  onOpenChange={() => toggleCollapsible(index)}
                  className="overflow-hidden border border-gray-200 rounded-lg px-6 py-4"
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex justify-between items-center cursor-pointer -mx-6 px-6 py-2 rounded">
                      <div className="flex items-center gap-3">
                        <Filter size={18} className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm font-semibold">
                          {criteria?.filterName}
                        </span>
                      </div>
                      {openCollapsibles[index] ? (
                        <ChevronUp size={18} className="text-blue-600" />
                      ) : (
                        <ChevronDown size={18} className="text-blue-600" />
                      )}
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="pt-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {criteria?.fields &&
                          Object.entries(criteria.fields).map(
                            ([key, value]: [string, any]) => (
                              <ValueLabel
                                key={key}
                                label={formatFieldName(key)}
                                value={value}
                              />
                            )
                          )}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SegmentDetails;
